import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import PredicationCard from '@/pages/home/PredicationCard'
import { HERO_IMAGE_URL } from '@/data/media'
import { DERNIERES_PREDICATIONS } from '@/data/predications'

const TOUTES_LES_SERIES = ['Toutes les séries', ...new Set(DERNIERES_PREDICATIONS.map((p) => p.serie))]
const TOUS_LES_PREDICATEURS = ['Tous les prédicateurs', ...new Set(DERNIERES_PREDICATIONS.map((p) => p.predicateur))]

export default function PredicationsPage() {
  const [recherche, setRecherche] = useState('')
  const [serie, setSerie] = useState(TOUTES_LES_SERIES[0])
  const [predicateur, setPredicateur] = useState(TOUS_LES_PREDICATEURS[0])

  const resultats = useMemo(() => {
    const q = recherche.trim().toLowerCase()
    return DERNIERES_PREDICATIONS.filter((p) => {
      const matchRecherche = !q || p.titre.toLowerCase().includes(q) || p.predicateur.toLowerCase().includes(q)
      const matchSerie = serie === TOUTES_LES_SERIES[0] || p.serie === serie
      const matchPredicateur = predicateur === TOUS_LES_PREDICATEURS[0] || p.predicateur === predicateur
      return matchRecherche && matchSerie && matchPredicateur
    })
  }, [recherche, serie, predicateur])

  return (
    <div>
      <section className="relative text-white px-6 py-16 md:py-20 text-center overflow-hidden">
        {/* Photo de l'assemblée — image Unsplash de PRÉVISUALISATION (voir data/media.ts). */}
        <img src={HERO_IMAGE_URL} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-linear-to-b from-cmp-navy-dark/90 via-cmp-navy/75 to-cmp-navy-dark/95" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 60% 50% at 20% 20%, rgba(201,147,47,0.2) 0%, transparent 100%)',
          }}
        />

        <div className="relative z-10 max-w-xl mx-auto">
          <p className="text-cmp-gold-light text-xs font-bold uppercase tracking-[0.2em] mb-3">Bibliothèque</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-balance">
            Toutes les prédications
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mt-4 max-w-md mx-auto">
            Réécoutez ou (re)découvrez nos messages, cherchez par prédicateur ou par série, et
            partagez-en un sur WhatsApp en un clic.
          </p>
        </div>
      </section>

      <section className="px-4 py-10 md:py-14 bg-cmp-cream">
        <div className="max-w-5xl mx-auto">
          {/* Recherche + filtres */}
          <div className="flex flex-col md:flex-row gap-3 mb-10">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Rechercher un titre ou un prédicateur…"
                className="w-full rounded-full border border-cmp-navy/15 bg-white pl-11 pr-4 py-3 text-sm text-cmp-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cmp-blue/40"
              />
            </div>

            <select
              value={serie}
              onChange={(e) => setSerie(e.target.value)}
              className="rounded-full border border-cmp-navy/15 bg-white px-4 py-3 text-sm text-cmp-navy focus:outline-none focus:ring-2 focus:ring-cmp-blue/40"
            >
              {TOUTES_LES_SERIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              value={predicateur}
              onChange={(e) => setPredicateur(e.target.value)}
              className="rounded-full border border-cmp-navy/15 bg-white px-4 py-3 text-sm text-cmp-navy focus:outline-none focus:ring-2 focus:ring-cmp-blue/40"
            >
              {TOUS_LES_PREDICATEURS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {resultats.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {resultats.map((predication) => (
                <PredicationCard key={predication.id} {...predication} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-cmp-navy font-bold">Aucune prédication ne correspond</p>
              <p className="text-gray-500 text-sm mt-1">Essayez une autre recherche ou réinitialisez les filtres.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
